import type { ScopedThreadRef } from "@amoreg/contracts";
import { type UseNavigateResult, useLocation, useNavigate } from "@tanstack/react-router";
import { createContext, createElement, type ReactNode, useContext, useMemo } from "react";

import type { DraftId } from "../../composerDraftStore";
import {
  selectActiveRightPanelSurface,
  selectThreadRightPanelState,
  useRightPanelStore,
} from "../../rightPanelStore";
import { buildDraftThreadRouteParams, buildThreadRouteParams } from "../../threadRoutes";

type ChatHostNavigate = UseNavigateResult<string>;

export interface ChatHostCitationLocation {
  readonly href: string;
  readonly key: string | undefined;
}

export interface ChatHostNavigation {
  openDraft(draftId: DraftId, orgId: string): Promise<void>;
  openThread(threadRef: ScopedThreadRef, orgId: string): Promise<void>;
}

interface ChatHostPages {
  openNewThreadPage(draftId: DraftId, orgId: string, options: { replace: boolean }): Promise<void>;
}

interface ChatHostEvents {
  /**
   * Whether a place on the page is the chat's: a key or paste event's target,
   * or the focused element. The chat page owns every place; an embed only its
   * own focus container, so the page around it keeps its keys and its focus.
   */
  owns(target: EventTarget | null): boolean;
  /**
   * Whether focus fallen to the body is still the chat's, so the chat's
   * composer takes it back when a chat mounts. The chat page's always is; an
   * embed's only when the embed held focus as its chat changed (the old
   * chat's composer or button went with it).
   */
  heldFocus(): boolean;
  /**
   * Runs just before a draft's first message starts its thread, and is
   * awaited: an embed files the thread under what it was opened from again,
   * since an unsent draft's filing may have lapsed.
   */
  prepareFirstSend(threadRef: ScopedThreadRef): Promise<void>;
}

export interface ExternalChatHostHandlers extends ChatHostNavigation, ChatHostEvents {
  /**
   * The unsent draft the embed shows for a thread, if any: a thread whose
   * first message was not sent has no page yet, so the full chat page opens
   * its draft instead.
   */
  draftOf(threadRef: ScopedThreadRef): DraftId | null;
}

export type ChatHost =
  | (ChatHostNavigation & ChatHostPages & ChatHostEvents & { readonly surfaces: "route" })
  | (ChatHostNavigation &
      ChatHostPages &
      ChatHostEvents & {
        readonly surfaces: "external";
        openChatPage(threadRef: ScopedThreadRef, orgId: string): Promise<void>;
      });

function threadPage(threadRef: ScopedThreadRef, orgId: string) {
  return { to: "/$org/$threadId" as const, params: buildThreadRouteParams(threadRef, orgId) };
}

function draftPage(draftId: DraftId, orgId: string) {
  return {
    to: "/$org/draft/$draftId" as const,
    params: buildDraftThreadRouteParams(draftId, orgId),
  };
}

function chatPages(navigate: ChatHostNavigate): ChatHostPages {
  return {
    openNewThreadPage: (draftId, orgId, { replace }) =>
      navigate({ ...draftPage(draftId, orgId), replace }),
  };
}

export function routeChatHost(navigate: ChatHostNavigate): ChatHost {
  return {
    surfaces: "route",
    openDraft: (draftId, orgId) => navigate(draftPage(draftId, orgId)),
    openThread: (threadRef, orgId) => navigate(threadPage(threadRef, orgId)),
    owns: () => true,
    heldFocus: () => true,
    prepareFirstSend: () => Promise.resolve(),
    ...chatPages(navigate),
  };
}

export function externalChatHost(
  navigate: ChatHostNavigate,
  handlers: ExternalChatHostHandlers,
): ChatHost {
  return {
    surfaces: "external",
    openDraft: handlers.openDraft,
    openThread: handlers.openThread,
    owns: handlers.owns,
    heldFocus: handlers.heldFocus,
    prepareFirstSend: handlers.prepareFirstSend,
    openChatPage: (threadRef, orgId) => {
      const draftId = handlers.draftOf(threadRef);
      return navigate(draftId ? draftPage(draftId, orgId) : threadPage(threadRef, orgId));
    },
    ...chatPages(navigate),
  };
}

export function useRouteChatHost(): ChatHost {
  const navigate = useNavigate();
  return useMemo(() => routeChatHost(navigate), [navigate]);
}

/** Pass stable handlers: the host is rebuilt whenever one of them changes. */
export function useExternalChatHost({
  openDraft,
  openThread,
  owns,
  heldFocus,
  prepareFirstSend,
  draftOf,
}: ExternalChatHostHandlers): ChatHost {
  const navigate = useNavigate();
  return useMemo(
    () => externalChatHost(navigate, { openDraft, openThread, owns, heldFocus, prepareFirstSend, draftOf }),
    [navigate, openDraft, openThread, owns, heldFocus, prepareFirstSend, draftOf],
  );
}

export function useRouteCitationLocation(): ChatHostCitationLocation {
  const href = useLocation({ select: (location) => location.href });
  const key = useLocation({
    select: (location) => location.state.assistantCitationActivation ?? location.state.__TSR_key,
  });
  return useMemo(() => ({ href, key }), [href, key]);
}

export const ChatHostContext = createContext<ChatHost | null>(null);

/**
 * How an embed dresses the chat. `bare` drops the chat's own header row and
 * the new-chat hero, so the embed's own bar is the only one; the composer
 * stays docked at the bottom.
 */
export interface ChatPresentation {
  readonly chrome: "full" | "bare";
  /** The composer's placeholder; the chat's own when absent. */
  readonly placeholder?: string;
  /** A short line above the composer, whatever the thread holds. */
  readonly note?: string;
  /** Shown above the composer while the thread has no messages; `send` sends a text as if typed. */
  readonly emptyState?: (send: (text: string) => void) => ReactNode;
}

export const FULL_CHAT_PRESENTATION: ChatPresentation = { chrome: "full" };

export const ChatPresentationContext = createContext<ChatPresentation>(FULL_CHAT_PRESENTATION);

export const useChatPresentation = (): ChatPresentation => useContext(ChatPresentationContext);
const ChatCitationLocationContext = createContext<ChatHostCitationLocation | null>(null);

export function RouteChatHostProvider({ children }: { readonly children: ReactNode }) {
  return createElement(
    ChatHostContext.Provider,
    { value: useRouteChatHost() },
    createElement(
      ChatCitationLocationContext.Provider,
      { value: useRouteCitationLocation() },
      children,
    ),
  );
}

export function useChatHost(): ChatHost {
  const host = useContext(ChatHostContext);
  if (host === null) {
    throw new Error("HostedChatView needs a ChatHostContext provider.");
  }
  return host;
}

/** Null outside the chat route: an embed's page location is not the chat's. */
export function useChatCitationLocation(): ChatHostCitationLocation | null {
  return useContext(ChatCitationLocationContext);
}

/** The right panel is the chat page's, so an embed reads it closed whatever the store persisted. */
export function useHostedRightPanel(host: ChatHost, threadRef: ScopedThreadRef | null) {
  const panelThreadRef = host.surfaces === "route" ? threadRef : null;
  const state = useRightPanelStore((store) =>
    selectThreadRightPanelState(store.byThreadKey, panelThreadRef),
  );
  const activeSurface = useRightPanelStore((store) =>
    selectActiveRightPanelSurface(store.byThreadKey, panelThreadRef),
  );
  return { state, activeSurface };
}

/** External mode records the surface before leaving, so the full chat page restores it. */
export function showChatSurface(
  host: ChatHost,
  threadRef: ScopedThreadRef,
  orgId: string | undefined,
  open: (threadRef: ScopedThreadRef) => void,
): void {
  if (host.surfaces === "route") {
    open(threadRef);
    return;
  }
  if (!orgId) return;
  open(threadRef);
  void host.openChatPage(threadRef, orgId);
}
